import { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import {
  listQuoteNoteTemplates,
  listClaimQuoteNotes,
  setClaimQuoteNotes,
} from "../../services/apiClient.js";

// Quote Notes — the legacy QuotePage bottom expander, modernised. The saved
// note lines show read-only; "Choose Notes" opens the template picker where
// ticking a HEADER selects its whole group (header + following non-header
// lines, expanded server-side). The chosen text will print on the quote
// report when that lands (phase 2).

function QuoteNotesSection({ claimId }) {
  const [templates, setTemplates] = useState([]);
  const [notes, setNotes] = useState([]);
  const [picking, setPicking] = useState(false);
  const [checked, setChecked] = useState(() => new Set());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listQuoteNoteTemplates().then(setTemplates).catch(() => {});
    listClaimQuoteNotes(claimId).then(setNotes).catch(() => setError("Unable to load quote notes."));
  }, [claimId]);

  // Group the ordered templates: each header owns the non-header lines after it.
  const groups = useMemo(() => {
    const out = [];
    let current = null;
    for (const t of templates) {
      if (t.is_header) {
        current = { header: t, children: [] };
        out.push(current);
      } else if (current) {
        current.children.push(t);
      }
    }
    return out;
  }, [templates]);

  function openPicker() {
    setChecked(
      new Set(notes.filter((n) => n.is_header && n.template_id).map((n) => Number(n.template_id)))
    );
    setError("");
    setPicking(true);
  }

  function toggle(headerId) {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(headerId)) next.delete(headerId);
      else next.add(headerId);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError("");
    try {
      setNotes(await setClaimQuoteNotes(claimId, [...checked]));
      setPicking(false);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save quote notes.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Quote Notes</h5>
        {!picking ? (
          <Button size="sm" variant="outline-primary" onClick={openPicker} disabled={templates.length === 0}
            data-hint="Tick the note groups to include on this quote's printout">
            Choose Notes
          </Button>
        ) : null}
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {picking ? (
        <>
          {groups.map(({ header, children }) => (
            <div key={header.id} className="border rounded px-3 py-2 mb-2">
              <Form.Check
                type="checkbox"
                id={`qn-${header.id}`}
                className="fw-semibold"
                label={header.description}
                checked={checked.has(Number(header.id))}
                onChange={() => toggle(Number(header.id))}
              />
              {children.map((c) => (
                <div key={c.id} className="small text-muted ms-4" style={{ whiteSpace: "pre-wrap" }}>
                  {c.description}
                </div>
              ))}
            </div>
          ))}
          {groups.length === 0 ? (
            <p className="text-muted mb-2">
              No note templates set up yet — add them under System Data → Quote Note Templates.
            </p>
          ) : null}
          <Button size="sm" className="me-2" onClick={save} disabled={busy}>Save</Button>
          <Button size="sm" variant="secondary" onClick={() => setPicking(false)} disabled={busy}>Cancel</Button>
        </>
      ) : notes.length > 0 ? (
        notes.map((n) => (
          <div key={n.id} className={n.is_header ? "fw-semibold mt-1" : "ms-3"} style={{ whiteSpace: "pre-wrap" }}>
            {n.description}
          </div>
        ))
      ) : (
        <p className="text-muted mb-0">
          No quote notes selected. Chosen notes will print at the bottom of the quote.
        </p>
      )}
    </>
  );
}

export default QuoteNotesSection;
